import { useEffect, useMemo, useRef } from "react";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";

const InstagramFeedSection = () => {
  const { content } = useSiteContent();
  const instagramRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const section = content.home.instagram;

  const slides = useMemo(() => [...section.images, ...section.images], [section.images]);
  const gridImages = useMemo(
    () => Array.from({ length: Math.max(8, section.images.length) }, (_, i) => section.images[i % section.images.length]),
    [section.images],
  );

  useEffect(() => {
    const el = instagramRef.current;
    if (!el) return;

    let rafId = 0;
    const speed = 0.45;

    const tick = () => {
      if (!isDraggingRef.current) {
        el.scrollLeft += speed;
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = 0;
        }
      }
      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, []);

  const onDragStart = (clientX: number) => {
    const el = instagramRef.current;
    if (!el) return;
    isDraggingRef.current = true;
    dragStartXRef.current = clientX;
    dragStartScrollRef.current = el.scrollLeft;
  };

  const onDragMove = (clientX: number) => {
    const el = instagramRef.current;
    if (!el || !isDraggingRef.current) return;
    const delta = clientX - dragStartXRef.current;
    el.scrollLeft = dragStartScrollRef.current - delta;
  };

  const onDragEnd = () => {
    isDraggingRef.current = false;
  };

  return (
    <section className="py-12 md:py-20 bg-[#1a1a1a] border-y border-zinc-800" id="instagram" {...editableAttrs("home.instagram", "object")}>
      <div className="md:hidden">
        <div className="px-6 text-center mb-8">
          <div className="flex flex-col items-center gap-3">
            <span className="w-12 h-0.5 bg-red-600 rounded-full" aria-hidden />
            <h2 className="text-white text-lg font-bold tracking-[0.35em]" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              INSTAGRAM
            </h2>
            <span className="w-12 h-0.5 bg-red-600 rounded-full" aria-hidden />
          </div>
          <a
            href={section.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-block text-white text-3xl font-bold tracking-tight hover:text-red-400 transition-colors"
            style={{ fontFamily: "Rajdhani, sans-serif" }}
            {...editableAttrs("home.instagram.title", "text")}
          >
            {section.title}
          </a>
        </div>
        <div className="grid grid-cols-4 gap-[1px] w-full bg-zinc-900" {...editableAttrs("home.instagram.images", "array")}>
          {gridImages.map((img, idx) => (
            <a
              key={`ig-mobile-${idx}`}
              href={section.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="relative aspect-square overflow-hidden bg-zinc-900"
            >
              <img
                src={img}
                alt="Instagram"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                draggable={false}
                {...editableAttrs(`home.instagram.images.${idx % Math.max(1, section.images.length)}`, "image")}
              />
            </a>
          ))}
        </div>
      </div>

      <div className="hidden md:block max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("home.instagram.subtitle", "text")}>
            {section.subtitle}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("home.instagram.title", "text")}>
            {section.title}
          </h2>
        </div>

        <style>{`.instagram-scroll::-webkit-scrollbar { display: none; }`}</style>
        <div
          ref={instagramRef}
          className="instagram-scroll overflow-x-auto pb-2 cursor-grab active:cursor-grabbing select-none"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          onMouseDown={(e) => onDragStart(e.clientX)}
          onMouseMove={(e) => onDragMove(e.clientX)}
          onMouseUp={onDragEnd}
          onMouseLeave={onDragEnd}
        >
          <div className="flex gap-1 min-w-max bg-zinc-900" {...editableAttrs("home.instagram.images", "array")}>
            {slides.map((img, idx) => (
              <div key={`instagram-${idx}`} className="w-[280px] flex-shrink-0">
                <div className="aspect-square w-full overflow-hidden">
                  <img
                    src={img}
                    alt="Instagram gönderisi"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    draggable={false}
                    {...editableAttrs(`home.instagram.images.${idx % Math.max(1, section.images.length)}`, "image")}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InstagramFeedSection;
